import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ErrorApi, fijarToken, hayToken } from '../api/cliente.js';

export interface PerfilMedico {
  id: string;
  nombre: string;
  email: string;
  matricula: { tipo: string; numero: string; jurisdiccion: string };
  refeps: { validada: boolean; consultadaEn: string | null; fuente?: string };
  firmaEnrolado: boolean;
  firmaProveedor: string;
  claveFirmaDefinida: boolean;
  otpSandbox?: string;
}

export interface UsuarioPortal {
  id: string;
  nombre: string;
  rol: 'RRHH' | 'SERVICIO_MEDICO';
  empresa: string;
  operador: string;
}

export interface PerfilOperador {
  id: string;
  nombre: string;
  email: string;
}

interface EstadoSesion {
  medico: PerfilMedico | null;
  portal: UsuarioPortal | null;
  operador: PerfilOperador | null;
  cargando: boolean;
  loginMedico: (email: string, password: string) => Promise<void>;
  loginPortal: (email: string, password: string) => Promise<void>;
  loginOperador: (email: string, password: string) => Promise<void>;
  salir: () => void;
  refrescarPerfil: () => Promise<void>;
}

const Ctx = createContext<EstadoSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [medico, setMedico] = useState<PerfilMedico | null>(null);
  const [portal, setPortal] = useState<UsuarioPortal | null>(null);
  const [operador, setOperador] = useState<PerfilOperador | null>(null);
  const [cargando, setCargando] = useState(true);

  // Al cargar, si hay token intentamos reconstruir el perfil del médico.
  useEffect(() => {
    const tipo = localStorage.getItem('ausentia.tipoSesion');
    const portalGuardado = localStorage.getItem('ausentia.portal');
    const operadorGuardado = localStorage.getItem('ausentia.operador');
    if (!hayToken()) {
      setCargando(false);
      return;
    }
    if (tipo === 'portal' && portalGuardado) {
      setPortal(JSON.parse(portalGuardado));
      setCargando(false);
      return;
    }
    if (tipo === 'operador' && operadorGuardado) {
      setOperador(JSON.parse(operadorGuardado));
      setCargando(false);
      return;
    }
    api
      .get<PerfilMedico>('/api/perfil')
      .then((perfil) => {
        localStorage.setItem('ausentia.medico', JSON.stringify(perfil));
        setMedico(perfil);
      })
      .catch((err) => {
        // Fallo de red (no HTTP): el médico está en terreno sin señal. La
        // sesión se hidrata desde la última copia local — como ya hacen
        // portal/operador — en vez de destruir el token.
        const medicoGuardado = localStorage.getItem('ausentia.medico');
        if (!(err instanceof ErrorApi) && medicoGuardado) {
          setMedico(JSON.parse(medicoGuardado));
          return;
        }
        // Error HTTP real (401 ya borró el token en cliente.ts): sesión fuera.
        fijarToken(null);
        localStorage.removeItem('ausentia.medico');
      })
      .finally(() => setCargando(false));
  }, []);

  async function loginMedico(email: string, password: string) {
    const r = await api.post<{ token: string; medico: PerfilMedico }>('/api/auth/login', {
      email,
      password,
    });
    fijarToken(r.token);
    localStorage.setItem('ausentia.tipoSesion', 'medico');
    localStorage.removeItem('ausentia.portal');
    const perfil = await api.get<PerfilMedico>('/api/perfil');
    localStorage.setItem('ausentia.medico', JSON.stringify(perfil));
    setMedico(perfil);
    setPortal(null);
  }

  async function loginPortal(email: string, password: string) {
    const r = await api.post<{ token: string; usuario: UsuarioPortal }>('/api/portal/login', {
      email,
      password,
    });
    fijarToken(r.token);
    localStorage.setItem('ausentia.tipoSesion', 'portal');
    localStorage.setItem('ausentia.portal', JSON.stringify(r.usuario));
    setPortal(r.usuario);
    setMedico(null);
    setOperador(null);
  }

  async function loginOperador(email: string, password: string) {
    const r = await api.post<{ token: string; operador: PerfilOperador }>('/api/operador/login', {
      email,
      password,
    });
    fijarToken(r.token);
    localStorage.setItem('ausentia.tipoSesion', 'operador');
    localStorage.setItem('ausentia.operador', JSON.stringify(r.operador));
    setOperador(r.operador);
    setMedico(null);
    setPortal(null);
  }

  function salir() {
    fijarToken(null);
    localStorage.removeItem('ausentia.tipoSesion');
    localStorage.removeItem('ausentia.medico');
    localStorage.removeItem('ausentia.portal');
    localStorage.removeItem('ausentia.operador');
    setMedico(null);
    setPortal(null);
    setOperador(null);
  }

  async function refrescarPerfil() {
    if (localStorage.getItem('ausentia.tipoSesion') === 'medico') {
      const perfil = await api.get<PerfilMedico>('/api/perfil');
      localStorage.setItem('ausentia.medico', JSON.stringify(perfil));
      setMedico(perfil);
    }
  }

  const valor = useMemo<EstadoSesion>(
    () => ({ medico, portal, operador, cargando, loginMedico, loginPortal, loginOperador, salir, refrescarPerfil }),
    [medico, portal, operador, cargando],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): EstadoSesion {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSesion fuera del proveedor');
  return ctx;
}
